/**
 * Transaction Form Page - WORLD-CLASS UX
 * 
 * Features:
 * - Rich header with context
 * - Progress indicator
 * - Auto-save draft (future)
 * - Keyboard shortcuts
 * - Smart validation feedback
 * - Smooth animations
 */

import { useEffect, useState } from 'react';
import { Stack, Container } from '../../components/primitives';
import { PageHeader } from '../../components/layout';
import { Receipt, ArrowLeft, Sparkles, AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { TransactionFormRedesigned } from './components/TransactionFormRedesigned';
import { TransactionFormSkeleton } from './components/TransactionFormSkeleton';
import { EditRecurrenceDialog, type EditOption } from './components/EditRecurrenceDialog';
import { useTransactions } from './hooks/useTransactions';
import { useReconciledTransactions } from './hooks/useReconciledTransactions';
import { useCompanyContext } from '../../contexts/CompanyContext';
import { useResponsive } from '../../hooks/useResponsive';
import type { CashFlowEntry } from '../../types/database';
import type { TransactionCreateData } from '../../lib/schemas/transaction.schema';
import { toast } from 'sonner';

interface TransactionFormPageProps {
  userId: string;
  transactionId?: string;
  onBack: () => void;
  mode?: 'new' | 'edit' | 'recurring' | 'duplicate';
  duplicateFromId?: string;
}

export function TransactionFormPage({
  userId,
  transactionId,
  onBack,
  mode = 'new',
  duplicateFromId,
}: TransactionFormPageProps) {
  const { selectedCompanyIds, selectedCompanies } = useCompanyContext();
  const isEditing = mode === 'edit' || !!transactionId;
  const isRecurring = mode === 'recurring';
  const isDuplicating = mode === 'duplicate' && !!duplicateFromId;
  const [transaction, setTransaction] = useState<CashFlowEntry | undefined>();
  const [duplicateData, setDuplicateData] = useState<Partial<CashFlowEntry> | undefined>();
  const [loadingTransaction, setLoadingTransaction] = useState(isEditing || isDuplicating);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<TransactionCreateData & { company_id: string | null }>();
  const { isMobile } = useResponsive();
  
  // Use first selected company for new transaction
  const defaultCompanyId = Array.isArray(selectedCompanyIds) && selectedCompanyIds.length > 0 
    ? selectedCompanyIds[0] 
    : selectedCompanyIds;
  
  const { 
    transactions,
    createTransaction, 
    updateTransaction,
    updateRecurringTransaction,
    isCreating, 
    isUpdating 
  } = useTransactions({
    companyIds: selectedCompanyIds,
    userId,
  });

  // Verificar se a transação está conciliada
  const { reconciledIds, isLoadingReconciled } = useReconciledTransactions(
    Array.isArray(selectedCompanyIds) ? selectedCompanyIds : [selectedCompanyIds]
  );
  
  const isReconciled = transactionId ? reconciledIds.has(transactionId) : false;

  // Debug logs
  console.log('[TransactionFormPage] 🔍 Debug:', {
    transactionId,
    isReconciled,
    reconciledIdsSize: reconciledIds.size,
    reconciledIds: Array.from(reconciledIds),
    isLoadingReconciled,
  });

  // Track if user has been warned about unreconciliation
  const [hasSeenUnreconcileWarning, setHasSeenUnreconcileWarning] = useState(false);

  // Load transaction for editing
  useEffect(() => {
    if (isEditing && transactionId) {
      const found = transactions.find(t => t.id === transactionId);
      if (found) {
        setTransaction(found);
        setLoadingTransaction(false);
      } else if (transactions.length > 0) {
        // Transaction not found
        toast.error('Lançamento não encontrado');
        onBack();
      }
    }
  }, [isEditing, transactionId, transactions, onBack]);

  // Load transaction for duplicating
  useEffect(() => {
    if (isDuplicating && duplicateFromId) {
      const found = transactions.find(t => t.id === duplicateFromId);
      if (found) {
        // Preparar dados para duplicação (remover IDs e datas de controle)
        const today = new Date().toISOString().split('T')[0];
        setDuplicateData({
          ...found,
          id: undefined,
          created_at: undefined,
          updated_at: undefined,
          entry_date: today,
          // Manter dados de recorrência se houver
          is_recurring: false, // Não duplicar como recorrente
          recurrence_group_id: null,
          frequency: null,
          recurrence_end_date: null,
        });
        setLoadingTransaction(false);
      } else if (transactions.length > 0) {
        toast.error('Lançamento não encontrado para duplicar');
        onBack();
      }
    }
  }, [isDuplicating, duplicateFromId, transactions, onBack]);

  // Keyboard shortcut: Ctrl+S to submit (handled by form)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Escape') {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack]);

  const handleSubmit = async (data: TransactionCreateData & { company_id: string | null }) => {
    try {
      console.log('[TransactionFormPage] Submitting transaction:', {
        is_recurring: data.is_recurring,
        frequency: data.frequency,
        recurrence_end_date: data.recurrence_end_date,
        main_category: data.main_category,
        category: data.category,
        subcategory: data.subcategory,
        full_data: data
      });

      if (isEditing && transaction) {
        // Check if this is a recurring transaction
        if (transaction.is_recurring && transaction.recurrence_group_id) {
          // Verificar se já existe uma opção salva (vinda da lista de transações)
          const savedOption = localStorage.getItem(`transaction_edit_option_${transaction.id}`) as EditOption | null;
          
          if (savedOption) {
            // Limpar do localStorage
            localStorage.removeItem(`transaction_edit_option_${transaction.id}`);
            
            // Aplicar a edição diretamente com a opção escolhida
            await updateRecurringTransaction({
              transaction,
              updates: data,
              option: savedOption,
            });
            onBack();
            return;
          }
          
          // Se não tem opção salva, mostrar dialog para escolher
          setPendingUpdates(data);
          setShowEditDialog(true);
          return; // Wait for user to choose option
        }
        
        // Non-recurring or single edit
        await updateTransaction({
          id: transaction.id,
          updates: data,
        });
      } else {
        await createTransaction(data as any);
      }
      
      // Navigate back immediately - mutations handle success feedback
      onBack();
    } catch (error) {
      // Error toast is handled by the mutation, but we can add extra context here if needed
      console.error('Error submitting transaction:', error);
    }
  };

  const handleEditRecurringConfirm = async (option: EditOption) => {
    if (!transaction || !pendingUpdates) return;

    try {
      await updateRecurringTransaction({
        transaction,
        updates: pendingUpdates,
        option,
      });
      setShowEditDialog(false);
      setPendingUpdates(undefined);
      onBack();
    } catch (error) {
      console.error('[TransactionFormPage] Error updating recurring:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar lançamento');
    }
  };

  // Calculate series count for dialog
  const seriesCount = transaction?.recurrence_group_id ? (() => {
    const seriesTransactions = transactions
      .filter(t => t.recurrence_group_id === transaction.recurrence_group_id)
      .sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || ''));
    
    const futureTransactions = seriesTransactions.filter(
      t => t.entry_date && transaction.entry_date && 
           t.entry_date >= transaction.entry_date
    );
    
    // Debug log
    console.log('[TransactionFormPage] Series count calculation:', {
      transactionId: transaction.id,
      transactionDate: transaction.entry_date,
      totalInSeries: seriesTransactions.length,
      futureCount: futureTransactions.length,
      allDates: seriesTransactions.map(t => t.entry_date),
      futureDates: futureTransactions.map(t => t.entry_date),
    });
    
    return {
      total: seriesTransactions.length,
      future: futureTransactions.length,
    };
  })() : { total: 0, future: 0 };

  if (loadingTransaction) {
    return <TransactionFormSkeleton />;
  }

  // Mobile: Form takes full viewport height (fixed layout)
  if (isMobile) {
  return (
      <div className="h-full flex flex-col">
        {/* Mobile Header - Fixed */}
        <div className="flex-none bg-background border-b px-4 py-3 flex items-center gap-3 shadow-sm">
            <Button variant="ghost" size="icon" onClick={onBack} className="h-10 w-10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-semibold text-lg leading-tight">
                {isEditing ? 'Editar' : 'Novo'} Lançamento
              </h1>
              {isEditing && transaction && (
                <p className="text-xs text-muted-foreground truncate">
                  {transaction.description}
                </p>
              )}
            </div>
          </div>

        {/* Reconciliation Warning - Mobile */}
        {isEditing && isReconciled && (
          <Alert variant="default" className="mx-4 mt-4 border-warning-500 bg-warning-50">
            <AlertTriangle className="h-4 w-4 text-warning-600" />
            <AlertTitle className="text-sm">⚠️ Lançamento Conciliado</AlertTitle>
            <AlertDescription className="text-xs">
              Este lançamento está vinculado a uma transação bancária. Ao salvar, a conciliação será <strong>removida automaticamente</strong>.
            </AlertDescription>
          </Alert>
        )}

        {/* Form - Fills remaining space */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <TransactionFormRedesigned
            initialData={isDuplicating ? duplicateData : transaction}
            companyId={defaultCompanyId}
            selectedCompanies={selectedCompanies}
            onSubmit={handleSubmit}
            onCancel={onBack}
            isSubmitting={isCreating || isUpdating}
            isMobile={isMobile}
            forceRecurring={isRecurring}
          />
        </div>
      </div>
    );
  }

  // Desktop: Standard layout matching all other pages (Container + Stack)
  return (
    <Container size="large" className="py-6 md:py-8">
      <Stack spacing="large">
        {/* Desktop: Enhanced Header */}
        <PageHeader
          icon={Receipt}
          title={
            isEditing 
              ? 'Editar Lançamento' 
              : isDuplicating 
                ? 'Duplicar Lançamento'
                : isRecurring
                  ? 'Novo Lançamento Recorrente'
                  : 'Novo Lançamento'
          }
          description={
            isEditing
              ? `Atualize as informações de "${transaction?.description || 'lançamento'}"`
              : isDuplicating
                ? `Baseado em "${duplicateData?.description || 'lançamento'}"`
                : isRecurring
                  ? 'Configure um lançamento que se repete automaticamente'
                  : 'Preencha os dados do novo lançamento financeiro'
          }
          actions={
            <Button variant="outline" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Cancelar
            </Button>
          }
        />

        {/* Reconciliation Warning - Desktop */}
        {isEditing && isReconciled && (
          <Alert variant="default" className="border-warning-500 bg-warning-50 dark:bg-warning-950">
            <AlertTriangle className="h-4 w-4 text-warning-600" />
            <AlertTitle>⚠️ Lançamento Conciliado</AlertTitle>
            <AlertDescription>
              Este lançamento está vinculado a uma transação bancária. Ao salvar qualquer alteração, a conciliação será <strong>automaticamente removida</strong> e você precisará refazer o matching na página de conciliação bancária.
            </AlertDescription>
          </Alert>
        )}

        {/* Tips Card - Desktop only */}
        {!isEditing && !isDuplicating && (
          <Alert className="border-primary/50 bg-primary/5">
            <Sparkles className="h-4 w-4 text-primary" />
            <AlertTitle>
              {isRecurring ? 'Lançamento Recorrente' : 'Dicas para preenchimento rápido'}
            </AlertTitle>
            <AlertDescription className="text-sm space-y-1 mt-2">
              {isRecurring ? (
                <>
                  <p>• Configure a frequência (diária, semanal, mensal, etc.)</p>
                  <p>• Defina a data de término da recorrência</p>
                  <p>• O sistema criará automaticamente todos os lançamentos</p>
                </>
              ) : (
                <>
                  <p>• Use <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Tab</kbd> para navegar entre campos</p>
                  <p>• Campos com autocomplete sugerem valores já cadastrados</p>
                  <p>• Campos marcados com * são obrigatórios</p>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Duplicate Info - Desktop only */}
        {isDuplicating && duplicateData && (
          <Alert className="border-blue-500/50 bg-blue-50 dark:bg-blue-950">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle>Duplicando Lançamento</AlertTitle>
            <AlertDescription>
              Os dados foram copiados de "{duplicateData.description}". 
              Ajuste o que for necessário e salve como novo lançamento.
            </AlertDescription>
          </Alert>
        )}

        {/* Reconciliation Warning - Desktop */}
        {isEditing && isReconciled && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-success-600" />
              Lançamento Conciliado com Transação Bancária
            </AlertTitle>
            <AlertDescription>
              Este lançamento está vinculado a uma transação bancária através da conciliação automática. 
              <strong className="block mt-1">
                ⚠️ Ao salvar qualquer alteração, a conciliação será automaticamente removida
              </strong> 
              e você precisará refazer o matching na tela de Conciliação Bancária.
            </AlertDescription>
          </Alert>
        )}

        {/* Editing Alert - Desktop only */}
        {isEditing && !isReconciled && transaction && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Modo de Edição</AlertTitle>
            <AlertDescription>
              Você está editando o lançamento "{transaction.description}"
            </AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <TransactionFormRedesigned
          initialData={isDuplicating ? duplicateData : transaction}
          companyId={defaultCompanyId}
          selectedCompanies={selectedCompanies}
          onSubmit={handleSubmit}
          onCancel={onBack}
          isSubmitting={isCreating || isUpdating}
          isMobile={isMobile}
          forceRecurring={isRecurring}
        />
      </Stack>

      {/* Edit Recurrence Dialog */}
      {transaction && showEditDialog && (
        <EditRecurrenceDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          transaction={transaction}
          seriesCount={seriesCount}
          onConfirm={handleEditRecurringConfirm}
          isUpdating={isUpdating}
        />
      )}
    </Container>
  );
}

// Helper function for date formatting (if not already imported)
function formatDate(dateString: string, format: 'short' | 'long' = 'short'): string {
  const date = new Date(dateString);
  if (format === 'long') {
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  }
  return date.toLocaleDateString('pt-BR');
}
